import { useState } from "react";
import { X, RefreshCw } from "lucide-react";
import { cn } from "@/utils/cn";
import { useAuth } from "@/hooks/useAuth";
import { GameTab } from "./gameTypes";
import { useGameState } from "./useGameState";
import { defaultGamePeriod, formatCurrency } from "./gameUtils";
import { GameWorldMap } from "./GameWorldMap";
import { GamePokedex } from "./GamePokedex";
import { GameBattleList } from "./GameBattleList";

interface GameScreenProps {
  onClose: () => void;
}

const TABS: { id: GameTab; label: string; emoji: string }[] = [
  { id: "mapa",     label: "MAPA",     emoji: "🗺" },
  { id: "pokedex",  label: "POKÉDEX",  emoji: "📖" },
  { id: "batalhas", label: "BATALHAS", emoji: "⚔" },
];

export function GameScreen({ onClose }: GameScreenProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<GameTab>("mapa");
  const [period] = useState(defaultGamePeriod);

  const gameState = useGameState(period.startDate, period.endDate);

  const pixelFont: React.CSSProperties = { fontFamily: "'Press Start 2P', monospace" };
  const xpPct = Math.min(100, (gameState.playerXp % 100));

  return (
    <div
      className="flex flex-col h-full overflow-hidden bg-gray-950 text-white"
      style={{ fontFamily: "'Press Start 2P', monospace" }}
    >
      {/* ── Game top bar ────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-gray-900 border-b-2 border-emerald-900"
        style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(52,211,153,0.02) 1px, rgba(52,211,153,0.02) 2px)" }}
      >
        {/* Title */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-base">🎮</span>
          <span className="text-emerald-400 text-[9px] hidden sm:block" style={pixelFont}>
            GESTOR MASTER
          </span>
        </div>

        {/* Player card */}
        <div className="flex items-center gap-3 border border-emerald-900 bg-emerald-950/30 px-3 py-1.5 flex-shrink-0">
          <span className="text-gray-400 text-[7px]" style={pixelFont}>
            {user?.displayName?.toUpperCase().slice(0, 10) ?? "GESTOR"}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-emerald-500 text-[7px]" style={pixelFont}>
              LV.{gameState.playerLevel}
            </span>
            <div className="w-12 h-1.5 bg-gray-800">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${xpPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="hidden md:flex items-center gap-4 flex-1">
          <StatBadge
            icon="⚔"
            label="BATALHAS"
            value={String(gameState.encounters.length)}
            danger={gameState.encounters.length > 0}
            pixelFont={pixelFont}
          />
          <StatBadge
            icon="📦"
            label="PEDIDOS"
            value={String(gameState.totalOrdersCount)}
            pixelFont={pixelFont}
          />
          <StatBadge
            icon="💰"
            label="VALOR"
            value={formatCurrency(gameState.totalOrdersValue)}
            pixelFont={pixelFont}
          />
        </div>

        {/* Period */}
        <div className="hidden lg:flex items-center gap-1 text-gray-700 text-[7px] font-mono flex-shrink-0">
          <span>{period.startDate}</span>
          <span>→</span>
          <span>{period.endDate}</span>
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="flex-shrink-0 ml-auto p-1.5 text-gray-600 hover:text-white hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-700"
          title="Fechar jogo"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ── Tab navigation ──────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex border-b border-gray-800 bg-gray-900">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const badge =
            tab.id === "batalhas" && gameState.encounters.length > 0
              ? gameState.encounters.length
              : tab.id === "pokedex" && gameState.creatures.length > 0
                ? gameState.creatures.length
                : null;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[7px] border-b-2 transition-all relative",
                isActive
                  ? "border-emerald-500 text-emerald-400 bg-emerald-950/30"
                  : "border-transparent text-gray-600 hover:text-gray-400 hover:bg-gray-800/50"
              )}
              style={pixelFont}
            >
              <span>{tab.emoji}</span>
              <span className="hidden sm:block">{tab.label}</span>
              {badge !== null && (
                <span
                  className={cn(
                    "text-[6px] px-1 py-0.5 ml-0.5",
                    tab.id === "batalhas" ? "bg-red-600" : "bg-emerald-700",
                    "text-white"
                  )}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}

        {/* Loading indicator */}
        {gameState.loading && (
          <div className="ml-auto flex items-center px-3">
            <RefreshCw className="w-3 h-3 text-emerald-600 animate-spin" />
          </div>
        )}
      </div>

      {/* ── Tab content ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden" style={{ fontFamily: "inherit" }}>
        {gameState.loading && (
          <LoadingScreen pixelFont={pixelFont} />
        )}

        {!gameState.loading && gameState.error && (
          <ErrorScreen error={gameState.error} pixelFont={pixelFont} />
        )}

        {!gameState.loading && !gameState.error && (
          <>
            {activeTab === "mapa" && <GameWorldMap zones={gameState.zones} />}
            {activeTab === "pokedex" && <GamePokedex creatures={gameState.creatures} />}
            {activeTab === "batalhas" && (
              <GameBattleList
                encounters={gameState.encounters}
                startDate={period.startDate}
                endDate={period.endDate}
              />
            )}
          </>
        )}
      </div>

      {/* ── Status bar ──────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center gap-4 px-4 py-1 bg-gray-900 border-t border-gray-800">
        <span className="text-gray-700 text-[6px] font-mono">
          ©2026 SEMOG ENGENHARIA
        </span>
        <span className="text-gray-700 text-[6px] font-mono">•</span>
        <span className="text-gray-700 text-[6px] font-mono">
          GESTOR MASTER v1.0
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-emerald-800 text-[6px] font-mono">ONLINE</span>
        </div>
      </div>
    </div>
  );
}

function StatBadge({
  icon,
  label,
  value,
  danger = false,
  pixelFont,
}: {
  icon: string;
  label: string;
  value: string;
  danger?: boolean;
  pixelFont: React.CSSProperties;
}) {
  return (
    <div className="flex items-center gap-1.5 border border-gray-800 px-2 py-1">
      <span className="text-[9px]">{icon}</span>
      <div>
        <div className="text-gray-700 text-[5px]" style={pixelFont}>{label}</div>
        <div
          className={cn("text-[8px]", danger ? "text-red-400" : "text-gray-300")}
          style={pixelFont}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function LoadingScreen({ pixelFont }: { pixelFont: React.CSSProperties }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-5">
      <div className="text-4xl animate-bounce">🎮</div>
      <div className="text-emerald-400 text-[9px]" style={pixelFont}>
        CARREGANDO MUNDO…
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="w-2 h-2 bg-emerald-600 animate-pulse"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function ErrorScreen({ error, pixelFont }: { error: string; pixelFont: React.CSSProperties }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="text-4xl">💀</div>
      <div className="text-red-400 text-[9px]" style={pixelFont}>ERRO AO CARREGAR</div>
      <p className="text-gray-500 text-[9px] font-mono text-center max-w-xs">{error}</p>
    </div>
  );
}
